import { useEffect, useRef, useState } from "react"
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react"

import ghanaIndependenceSquare from "@/client/assets/auth/ghana-independence-square.jpg"
import ghanaAccraSkyline from "@/client/assets/auth/ghana-accra-skyline.jpg"
import africaSafariSunrise from "@/client/assets/auth/africa-safari-sunrise.jpg"
import africaBaobab from "@/client/assets/auth/africa-baobab.jpg"
import africaLandscape from "@/client/assets/auth/africa-landscape.jpg"
import africaVillage from "@/client/assets/auth/africa-village.jpg"
import africaCapeTown from "@/client/assets/auth/africa-cape-town-inspiring.jpg"

const AUTH_BACKGROUNDS = [
  { src: ghanaIndependenceSquare, alt: "Independence Arch, Accra, Ghana" },
  { src: ghanaAccraSkyline, alt: "Accra skyline, Ghana" },
  { src: africaSafariSunrise, alt: "African savanna sunrise" },
  { src: africaBaobab, alt: "Acacia tree at sunset on the African plains" },
  { src: africaLandscape, alt: "Mount Kilimanjaro rising above the savanna" },
  { src: africaVillage, alt: "Giraffe and acacia at golden hour in Africa" },
  { src: africaCapeTown, alt: "Cape Town and Table Mountain, South Africa" },
] as const

const ROTATE_MS = 8_000
const COUNT = AUTH_BACKGROUNDS.length

/**
 * Full-bleed rotating Ghana / Africa photography for the auth hero panel.
 * Images are local Unsplash downloads (no watermarks).
 */
export function AuthHeroBackground() {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const resumeTimer = useRef<number | null>(null)

  const goTo = (next: number) => {
    setIndex(((next % COUNT) + COUNT) % COUNT)
  }

  const nudge = (delta: number) => {
    goTo(index + delta)
    // Brief pause after manual control, then resume autoplay
    setPaused(true)
    if (resumeTimer.current) window.clearTimeout(resumeTimer.current)
    resumeTimer.current = window.setTimeout(() => setPaused(false), ROTATE_MS)
  }

  useEffect(() => {
    if (paused) return
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % COUNT)
    }, ROTATE_MS)
    return () => window.clearInterval(id)
  }, [paused])

  useEffect(() => {
    const next = AUTH_BACKGROUNDS[(index + 1) % COUNT]
    const img = new Image()
    img.src = next.src
  }, [index])

  useEffect(() => {
    return () => {
      if (resumeTimer.current) window.clearTimeout(resumeTimer.current)
    }
  }, [])

  return (
    <div className="absolute inset-0 overflow-hidden bg-black">
      {AUTH_BACKGROUNDS.map((slide, i) => (
        <img
          key={slide.src}
          src={slide.src}
          alt={slide.alt}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}

      {/* Soft vignette so brand + disclaimer stay readable */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/35" />

      {/* Controls — top-right so they clear the brand mark at the bottom */}
      <div className="absolute right-6 top-6 z-20 flex items-center gap-2">
        <button
          type="button"
          onClick={() => nudge(-1)}
          aria-label="Previous image"
          className="flex size-9 items-center justify-center rounded-full border border-white/25 bg-black/40 text-white backdrop-blur-sm transition hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          <ChevronLeft className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => setPaused((p) => !p)}
          aria-label={paused ? "Resume slideshow" : "Pause slideshow"}
          className="flex size-9 items-center justify-center rounded-full border border-white/25 bg-black/40 text-white backdrop-blur-sm transition hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          {paused ? <Play className="size-3.5" /> : <Pause className="size-3.5" />}
        </button>
        <button
          type="button"
          onClick={() => nudge(1)}
          aria-label="Next image"
          className="flex size-9 items-center justify-center rounded-full border border-white/25 bg-black/40 text-white backdrop-blur-sm transition hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>

      {/* Dot indicators — bottom-right so they clear the brand mark at bottom-left */}
      <div
        className="absolute bottom-10 right-10 z-20 flex items-center gap-1.5"
        role="tablist"
        aria-label="Background images"
      >
        {AUTH_BACKGROUNDS.map((slide, i) => (
          <button
            key={slide.src}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`Show ${slide.alt}`}
            onClick={() => {
              goTo(i)
              setPaused(true)
              if (resumeTimer.current) window.clearTimeout(resumeTimer.current)
              resumeTimer.current = window.setTimeout(() => setPaused(false), ROTATE_MS)
            }}
            className={`h-1.5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
              i === index
                ? "w-6 bg-white"
                : "w-1.5 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </div>
  )
}
